import React, { useState, useRef, useEffect, useMemo } from "react";
import CoraaHeroSection from "@/components/cora/CoraaHeroSection";
import SuggestedPromptsBar from "@/components/cora/SuggestedPromptsBar";
import SmartSidebar from "@/components/cora/SmartSidebar";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  BotIcon,
  SendIcon,
  Loader2,
  Settings,
  Upload,
  Database,
  Trash2,
  ExternalLink,
  X,
  RefreshCw,
  Download,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useRealtimeRun, TriggerAuthContext } from "@trigger.dev/react-hooks";
import { Progress } from "@/components/ui/progress";

interface Message {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: Date;
  isHtml?: boolean;
  type?: "processing" | "error" | "success";
  progress?: number;
}

interface CoraaMessage extends Message {
  // Extended message type for CORAA-specific features
}

interface TrainingDataItem {
  document_id: string;
  source_url: string;
  created_at: string;
}


interface PaginationState {
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
}

interface AskCoraProps {
  session: any;
  profile: any;
}

interface UploadResult {
  success: boolean;
  message: string;
  runId?: string;
  publicAccessToken?: string;
  progress?: number;
}

// Function to detect if content is HTML or contains HTML code blocks
const containsHtml = (content: string): boolean => {
  // Check for direct HTML tags
  const htmlRegex = /<\/?[a-z][\s\S]*>/i;

  // Check for markdown HTML code blocks
  const htmlCodeBlockRegex = /```html[\s\S]*?```/i;

  return htmlRegex.test(content) || htmlCodeBlockRegex.test(content);
};

// Function to decode Unicode escape sequences to actual emojis
const decodeUnicodeEscapes = (content: string): string => {
  return content.replace(/\\u\{([0-9a-fA-F]+)\}/g, (match, hex) => {
    const codePoint = parseInt(hex, 16);
    return String.fromCodePoint(codePoint);
  });
};

// Function to detect if URL is a media file
const isMediaUrl = (url: string): { isMedia: boolean; type: 'image' | 'video' | null } => {
  const imageExtensions = /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)(\?.*)?$/i;
  const videoExtensions = /\.(mp4|webm|ogg|avi|mov|wmv|flv|mkv)(\?.*)?$/i;
  
  if (imageExtensions.test(url)) {
    return { isMedia: true, type: 'image' };
  }
  if (videoExtensions.test(url)) {
    return { isMedia: true, type: 'video' };
  }
  
  return { isMedia: false, type: null };
};

// Component to render media preview with download link - Memoized to prevent re-renders
const MediaPreview = React.memo<{ url: string; alt?: string }>(({ url, alt }) => {
  const { isMedia, type } = useMemo(() => isMediaUrl(url), [url]);
  
  if (!isMedia) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 hover:underline"
      >
        {url}
      </a>
    );
  }

  const downloadFilename = useMemo(() => url.split('/').pop() || 'download', [url]);

  return (
    <div className="my-3 space-y-2">
      {type === 'image' ? (
        <div className="relative group">
          <img
            src={url}
            alt={alt || 'Image'}
            className="max-w-full max-h-80 object-contain rounded-lg border border-gray-200 shadow-sm cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => window.open(url, '_blank')}
            onError={(e) => {
              // Fallback to link if image fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent) {
                parent.innerHTML = `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 hover:underline">${url}</a>`;
              }
            }}
          />
        </div>
      ) : type === 'video' ? (
        <div className="relative">
          <video
            controls
            className="max-w-full max-h-80 rounded-lg border border-gray-200 shadow-sm"
            onError={(e) => {
              // Fallback to link if video fails to load
              const target = e.target as HTMLVideoElement;
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent) {
                parent.innerHTML = `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 hover:underline">${url}</a>`;
              }
            }}
          >
            <source src={url} />
            Your browser does not support the video tag.
            <a href={url} target="_blank" rel="noopener noreferrer">
              View Video
            </a>
          </video>
        </div>
      ) : null}
      
      <div className="flex items-center gap-2 text-sm">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          View Original
        </a>
        <span className="text-gray-400">•</span>
        <a
          href={url}
          download={downloadFilename}
          className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
        >
          <Download className="h-3 w-3" />
          Download
        </a>
      </div>
    </div>
  );
});

// Function to process content and extract HTML from code blocks if needed
const processHtmlContent = (content: string): string => {
  // Check if content contains a markdown HTML code block
  const htmlCodeBlockRegex = /```html([\s\S]*?)```/i;
  const match = content.match(htmlCodeBlockRegex);

  if (match && match[1]) {
    // Extract the HTML from inside the code block
    return match[1].trim();
  }

  // If no code block or multiple blocks, return the original content
  return content;
};

// Component to render HTML content in an iframe - Memoized to prevent re-renders
const HtmlIframe = React.memo<{ content: string }>(({ content }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(300); // Default height

  const processedContent = processHtmlContent(content);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    // Create a complete HTML document with necessary styles
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <style>
            html, body {
              margin: 0;
              padding: 0;
              font-family: system-ui, -apple-system, sans-serif;
              color: #333;
              width: 100% !important;
              max-width: 100% !important;
              box-sizing: border-box;
              overflow-x: hidden;
            }
            body {
              padding: 20px;
              margin: 0 auto;
              max-width: 100%;
            }
            canvas, iframe, img, video {
              width: 100% !important;
              max-width: 100% !important;
              height: auto !important;
              display: block;
              margin: 0 auto;
            }
            .chart-container {
              width: 100% !important;
              position: relative;
              margin: 0 auto;
            }
          </style>
          <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
        </head>
        <body>
          ${processedContent}
        </body>
      </html>
    `;

    // Set the content of the iframe
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(htmlContent);
      doc.close();

      // Adjust iframe height after content loads
      const resizeObserver = new ResizeObserver(() => {
        if (doc.body) {
          const newHeight = doc.body.scrollHeight;
          setHeight(newHeight + 20); // Add some padding
        }
      });

      if (doc.body) {
        resizeObserver.observe(doc.body);
      }

      return () => {
        if (doc.body) {
          resizeObserver.disconnect();
        }
      };
    }
  }, [processedContent]);

  return (
    <div className="w-full overflow-x-auto">
      <iframe
        ref={iframeRef}
        title="HTML Content"
        sandbox="allow-scripts allow-same-origin"
        style={{
          width: "100%",
          minWidth: "100%",
          height: `${height}px`,
          border: "none",
          borderRadius: "4px",
          backgroundColor: "#fff",
        }}
      />
    </div>
  );
});

// Memoized message component to prevent unnecessary re-renders of individual messages
const MessageComponent = React.memo<{
  message: CoraaMessage & { hasHtml: boolean };
  setSelectedImage: (image: string | null) => void;
  markdownComponents: any;
}>(({ message, setSelectedImage, markdownComponents }) => {
  return (
    <div
      className={`flex ${
        message.role === "user"
          ? "justify-end"
          : "justify-start"
      }`}
    >
      <div
        className={`${
          message.role === "user"
            ? "max-w-[95%]"
            : message.hasHtml
            ? "w-full max-w-full"
            : message.type === "processing" ||
              message.type === "success" ||
              message.type === "error"
            ? "max-w-[80%] w-full"
            : "max-w-[80%]"
        } rounded-lg ${
          message.role === "user"
            ? "bg-primary text-primary-foreground p-4"
            : message.hasHtml
            ? "bg-muted p-0"
            : message.type === "processing"
            ? "bg-blue-50 border border-blue-100 p-4"
            : message.type === "success"
            ? "bg-green-50 border border-green-100 p-4"
            : message.type === "error"
            ? "bg-red-50 border border-red-100 p-4"
            : "bg-muted px-4 py-4"
        }`}
      >
{/* Handle CORAA streaming states */}
        {message.isStreaming && message.streamingState ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {message.streamingState.phase === "connecting" && (
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                )}
                {message.streamingState.phase === "thinking" && (
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                  </div>
                )}
                {message.streamingState.phase === "tool_execution" && (
                  <div className="w-4 h-4 border-2 border-blue-500 border-r-transparent rounded-full animate-spin" />
                )}
                {message.streamingState.phase === "generating" && (
                  <Loader2 className="h-4 w-4 animate-spin text-green-500" />
                )}
                {message.streamingState.phase === "error" && (
                  <svg className="h-4 w-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
                <span className="font-medium text-sm">
                  {message.streamingState.phase === "connecting" && "Connecting to CORAA"}
                  {message.streamingState.phase === "thinking" && "CORAA is thinking"}
                  {message.streamingState.phase === "tool_execution" && "Using tools"}
                  {message.streamingState.phase === "generating" && "Generating response"}
                  {message.streamingState.phase === "complete" && "Complete"}
                  {message.streamingState.phase === "error" && "Error occurred"}
                </span>
              </div>
            </div>
            
            {/* Show current message */}
            <div className="text-sm text-muted-foreground">
              {message.streamingState.message}
            </div>
            
            {/* Show temporary HTML if available */}
            {message.streamingState.tempHtml && (
              <div 
                className="mt-3"
                dangerouslySetInnerHTML={{ __html: message.streamingState.tempHtml }}
              />
            )}
            
            {/* Show progress bar */}
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Progress</span>
                <span>{Math.round(message.streamingState.progress)}%</span>
              </div>
              <Progress
                value={message.streamingState.progress}
                className="h-2 [&>div]:bg-gradient-to-r [&>div]:from-blue-500 [&>div]:to-green-500"
              />
            </div>
            
            {/* Show partial content if available */}
            {message.content && (
              <div className="mt-3 p-3 bg-muted/50 rounded border-l-4 border-l-green-500">
                <div className="text-xs text-muted-foreground mb-1">Preview:</div>
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={markdownComponents}
                  >
                    {message.content}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        ) : message.type === "processing" ||
        message.type === "success" ||
        message.type === "error" ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                {message.type === "processing" && (
                  <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                )}
                {message.type === "success" && (
                  <svg
                    className="h-4 w-4 text-green-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
                {message.type === "error" && (
                  <svg
                    className="h-4 w-4 text-red-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                )}
                <span className="font-medium">
                  {message.type === "processing"
                    ? "Processing Document"
                    : message.type === "success"
                    ? "Document Processed"
                    : "Processing Failed"}
                </span>
              </div>
            </div>
            <div className="text-sm">{message.content}</div>
            {message.type === "processing" &&
              message.progress !== undefined && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Processing</span>
                    <span>
                      {Math.round(message.progress)}%
                    </span>
                  </div>
                  <Progress
                    value={message.progress}
                    className="h-2 [&>div]:bg-[#2E7D32]"
                  />
                </div>
              )}
          </div>
        ) : !message.hasHtml ? (
          <div className="flex items-center gap-2 mb-1">
            {message.role === "assistant" && (
              <Avatar className="h-6 w-6">
                <AvatarImage src="/images/coraa-mascot.png" />
                <AvatarFallback className="bg-primary/10 text-primary">
                  <BotIcon className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>
            )}
            <span className="text-xs opacity-70">
              {message.role === "user" ? "You" : "Coraa"} •{" "}
              {format(message.timestamp, "h:mm a")}
            </span>
          </div>
        ) : null}
        <div className="prose prose-sm dark:prose-invert max-w-none">
          {message.hasHtml ? (
            <HtmlIframe content={message.content} />
          ) : (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {message.content}
            </ReactMarkdown>
          )}
        </div>
      </div>
    </div>
  );
});

const AskCora: React.FC<AskCoraProps> = ({ session, profile }) => {
  // ... existing state

  const [messages, setMessages] = useState<CoraaMessage[]>([
    {
      id: "1",
      role: "assistant",
      content: decodeUnicodeEscapes(
        "Hi, I'm Coraa, your CSR assistant. I can help you with information about corporate social responsibility, volunteer opportunities, impact measurement, and more. How can I assist you today?"
      ),
      timestamp: new Date(),
      isHtml: false,
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [streamingStatus, setStreamingStatus] = useState<{
    message: string;
    phase: string;
    toolName?: string;
    toolArgs?: any;
    metadata?: any;
  } | null>(null);
  
  // Configuration: can be toggled via settings or environment variable
  const [streamingEnabled, setStreamingEnabled] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [trainingData, setTrainingData] = useState<TrainingDataItem[]>([]);
  const [isLoadingTrainingData, setIsLoadingTrainingData] = useState(false);
  const [isDeletingTrainingData, setIsDeletingTrainingData] = useState<
    string | null
  >(null);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: 10,
    total: 0,
    hasMore: false,
  });
  const [showTrainingDataModal, setShowTrainingDataModal] = useState(false);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Memoized ReactMarkdown components to prevent unnecessary re-renders
  const markdownComponents = useMemo(() => ({
    a: ({ node, ...props }: any) => {
      const href = props.href || '';
      const { isMedia } = isMediaUrl(href);
      
      if (isMedia) {
        return (
          <MediaPreview 
            url={href} 
            alt={props.children?.toString() || 'Media'} 
          />
        );
      }
      
      return (
        <a
          {...props}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 hover:underline"
        />
      );
    },
    img: ({ node, ...props }: any) => (
      <div className="image-thumbnail-container cursor-pointer my-2">
        <img
          {...props}
          alt={props.alt || "Image"}
          onClick={() => setSelectedImage(props.src || null)}
          className="max-w-[150px] max-h-[150px] object-cover rounded-md hover:opacity-90 transition-opacity border border-gray-200 shadow-sm"
          style={{
            width: "150px",
            height: "150px",
            objectFit: "cover",
          }}
        />
      </div>
    ),
  }), [setSelectedImage]);

  // Memoized messages with their HTML status to prevent recalculation
  const processedMessages = useMemo(() => {
    return messages.map(message => ({
      ...message,
      hasHtml: message.isHtml || containsHtml(message.content)
    }));
  }, [messages]);

  // Scroll to bottom of messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [messages, isLoading]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  // Handle file upload - exact implementation from GraphPage.tsx
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);
      // Automatically trigger upload
      const formData = new FormData();
      formData.append("file", selectedFile);

      if (!session?.access_token) {
        setUploadResult({
          success: false,
          message: "Please log in to upload documents",
        });
        return;
      }

      setIsUploading(true);
      setUploadResult(null);

      try {
        console.log("Starting file upload process...");
        // Get user ID from session
        const userId = session.user.id;
        console.log("User ID:", userId);

        // Upload to Supabase storage
        const fileExt = selectedFile.name.split(".").pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const filePath = `${userId}/Knowledgebase/${fileName}`;
        console.log("File path:", filePath);

        console.log("Setting Supabase session...");
        await supabase.auth.setSession({
          access_token: session.access_token,
          refresh_token: session.refresh_token,
        });
        console.log("Supabase session set, uploading file...");
        // Upload file to Supabase storage
        const { error: uploadError } = await supabase.storage
          .from("nonprofit-cloud")
          .upload(filePath, selectedFile);

        if (uploadError) throw uploadError;

        // Get the public URL of the uploaded file
        const {
          data: { publicUrl },
        } = supabase.storage.from("nonprofit-cloud").getPublicUrl(filePath);

        const response = await fetch(
          `${
            import.meta.env.VITE_CC_API_URL
          }/v1/tools/knowledgebase/cora/training`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              content_type: "document",
              file_url: publicUrl,
            }),
          }
        );

        const result = await response.json();

        setUploadResult({
          success: response.ok,
          message:
            result.message ||
            (response.ok
              ? "Document uploaded and processing started!"
              : "Failed to upload document"),
          runId: result.data?.id,
          publicAccessToken: result.data?.publicAccessToken,
        });
      } catch (error) {
        console.error("Error uploading document:", error);
        setUploadResult({
          success: false,
          message: "An error occurred while uploading the document",
        });
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        setFile(null);
      }
    }
  };

  // Handle clear training data
  const handleClearTrainingData = async () => {
    if (!session?.access_token) return;

    try {
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/tools/knowledgebase/cora`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (response.ok) {
        setShowClearConfirmation(false);

        // Add a message from Cora about clearing the data
        const assistantMessage: Message = {
          id: Date.now().toString(),
          role: "assistant",
          content: decodeUnicodeEscapes(
            "I've cleared all training data. My knowledge has been reset to default settings."
          ),
          timestamp: new Date(),
          isHtml: false,
        };

        setMessages((prev) => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error("Error clearing training data:", error);
    }
  };

  // Separate function to fetch checkpoints to avoid recursion
  const fetchCheckpoints = async (page: number) => {
    if (!session?.access_token) return;

    // Always show the modal first to provide immediate feedback
    if (page === 1) {
      setShowTrainingDataModal(true);
    }

    setIsLoadingTrainingData(true);

    if (page === 1) {
      setShowTrainingDataModal(true);
    }

    setIsLoadingTrainingData(true);
    try {
      const limit = pagination.limit;
      const response = await fetch(
        `${
          import.meta.env.VITE_CC_API_URL
        }/v1/tools/knowledgebase/cora/checkpoints?page=${page}&limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();

        // Handle nested data structure
        const resultData = result.data || [];
        if (page === 1) {
          // Reset data for first page
          setTrainingData(resultData);
        } else {
          // Append data for subsequent pages
          setTrainingData((prev) => [...prev, ...resultData]);
        }

        // Update pagination state with nested metadata
        setPagination({
          page,
          limit,
          total: result.pagination.total || 0,
          hasMore: result.pagination.totalPages > page || false,
        });
      } else {
        console.error("Error fetching checkpoints:", await response.text());
      }
    } catch (error) {
      console.error("Error fetching checkpoints:", error);
    } finally {
      setIsLoadingTrainingData(false);
    }
  };

  // Load more training data - matching GraphPage.tsx implementation
  const loadMoreTrainingData = () => {
    if (pagination.hasMore && !isLoadingTrainingData) {
      fetchCheckpoints(pagination.page + 1);
    }
  };

  // Delete training data item - matching GraphPage.tsx implementation
  const handleDeleteTrainingData = async (id: string) => {
    if (!session?.access_token) return;

    setIsDeletingTrainingData(id);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_CC_API_URL}/v1/tools/knowledgebase/cora/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      if (response.ok) {
        // Remove the deleted item from the list
        setTrainingData(trainingData.filter((item) => item.document_id !== id));

        // Update pagination total count
        setPagination((prev) => ({
          ...prev,
          total: Math.max(0, prev.total - 1),
        }));

        setUploadResult({
          success: true,
          message: "Training data removed successfully",
        });

        // Refresh the graph data if needed
        if (trainingData.length <= 1) {
          // If we're removing the last item, refresh the graph
          setTimeout(() => {
            fetchCheckpoints(1);
          }, 500);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setUploadResult({
          success: false,
          message: errorData.message || "Failed to remove training data",
        });
      }
    } catch (error) {
      console.error("Error deleting training data:", error);
      setUploadResult({
        success: false,
        message: "An error occurred while removing training data",
      });
    } finally {
      setIsDeletingTrainingData(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!input.trim() || isLoading) return;

    // Add user message
    const userMessage: Message = {
      id: `user_${Date.now()}`,
      role: "user",
      content: input.trim(),
      timestamp: new Date(),
      isHtml: false,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsLoading(true);

    // Clear any previous streaming status
    setStreamingStatus(null);

    try {

      // Determine if we should use streaming (configurable)
      const useStreaming = streamingEnabled;
      
      // Call your API endpoint
      const response = await fetch(
        `${import.meta.env.VITE_CC_CORAA_API_URL}/v1/chat`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            query: input.trim(),
            threadId: session.user.id,
            stream: useStreaming,
            channel: "web",
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to get response");
      }

      // Check if response is streaming or regular JSON
      const contentType = response.headers.get('content-type');
      const isStreamingResponse = contentType?.includes('text/event-stream') || 
                                  (useStreaming && response.body?.getReader);

      if (isStreamingResponse && response.body) {
        // Handle Server-Sent Events streaming
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let hasReceivedData = false;

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                const data = line.slice(6).trim();
                if (data === '[DONE]') break;
                
                try {
                  const parsed = JSON.parse(data);
                  hasReceivedData = true;
                  
                  // Handle tool call events
                  if (parsed.type === 'tool_call') {
                    setStreamingStatus({
                      message: parsed.content,
                      phase: "tool_calling",
                      toolName: parsed.metadata?.tool_name,
                      toolArgs: parsed.metadata?.tool_args,
                      metadata: parsed.metadata
                    });
                  }
                  // Handle tool result events
                  else if (parsed.type === 'tool_result') {
                    setStreamingStatus({
                      message: parsed.content,
                      phase: "tool_completed",
                      toolName: parsed.metadata?.tool_name,
                      metadata: parsed.metadata
                    });
                  }
                  // Handle thinking events
                  else if (parsed.type === 'thinking') {
                    setStreamingStatus({
                      message: parsed.content,
                      phase: "thinking"
                    });
                  }
                  // Handle agent thinking events
                  else if (parsed.type === 'agent_thinking') {
                    setStreamingStatus({
                      message: parsed.content || "Thinking...",
                      phase: "thinking"
                    });
                  }
                  // Handle agent response events
                  else if (parsed.type === 'agent_response') {
                    setStreamingStatus({
                      message: "Generating response...",
                      phase: "generating"
                    });
                  }
                  // Handle partial content updates
                  else if (parsed.type === 'partial') {
                    setStreamingStatus({
                      message: "Generating response...",
                      phase: "generating"
                    });
                  }
                  // Handle final content
                  else if (parsed.type === 'final') {
                    // Clear streaming status and add final message
                    setStreamingStatus(null);
                    
                    let content = parsed.content || "I'm sorry, I couldn't process your request. Please try again.";
                    content = decodeUnicodeEscapes(content);
                    const isHtml = containsHtml(content);
                    
                    const assistantMessage: CoraaMessage = {
                      id: `assistant_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                      role: "assistant",
                      content: content,
                      timestamp: new Date(),
                      isHtml: isHtml,
                    };

                    setMessages((prev) => [...prev, assistantMessage]);
                  }
                  // Handle error events
                  else if (parsed.type === 'error') {
                    setStreamingStatus(null);
                    
                    const errorMessage: CoraaMessage = {
                      id: `assistant_error_${Date.now()}`,
                      role: "assistant",
                      content: parsed.content || "An error occurred while processing your request.",
                      timestamp: new Date(),
                      isHtml: false,
                    };
                    
                    setMessages((prev) => [...prev, errorMessage]);
                  }
                  // Handle keep-alive pings (silent - no UI update needed)
                  else if (parsed.type === 'keep_alive') {
                    // Keep-alive ping to maintain connection - no action needed
                    console.log('🏓 Keep-alive ping received');
                  }
                } catch (parseError) {
                  console.error('Error parsing SSE data:', parseError);
                }
              }
            }
          }

          // If no data received via streaming, fallback to non-streaming
          if (!hasReceivedData) {
            throw new Error("No streaming data received, falling back to non-streaming");
          }
        } catch (streamError) {
          console.warn('Streaming failed, falling back to non-streaming response', streamError);
          // The error handler below will handle the fallback
          throw streamError;
        }
      } else {
        // Handle non-streaming response
        console.log('Handling non-streaming response');
        setStreamingStatus(null); // Clear any streaming status
        
        const data = await response.json();
        
        // Add assistant message from non-streaming response
        let content = data.data || "I'm sorry, I couldn't process your request. Please try again.";
        content = decodeUnicodeEscapes(content);
        const isHtml = containsHtml(content);
        
        const assistantMessage: CoraaMessage = {
          id: `assistant_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          role: "assistant",
          content: content,
          timestamp: new Date(),
          isHtml: isHtml,
        };

        setMessages((prev) => [...prev, assistantMessage]);
      }
    } catch (error) {
      console.error("Error:", error);

      // Clear streaming status and add error message
      setStreamingStatus(null);
      
      const errorMessage: CoraaMessage = {
        id: `assistant_error_${Date.now()}`,
        role: "assistant",
        content: "I'm sorry, there was an error processing your request. Please try again later.",
        timestamp: new Date(),
        isHtml: false,
      };

      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setStreamingStatus(null);
      // Focus on the text box after receiving a response
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 100);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Clear the conversation and start a new one
  const handleClearConversation = () => {
    // Reset to just the welcome message
    setMessages([
      {
        id: Date.now().toString(),
        role: "assistant",
        content: decodeUnicodeEscapes(
          "Hi, I'm Cora, your CSR assistant. I can help you with information about corporate social responsibility, volunteer opportunities, impact measurement, and more. How can I assist you today?"
        ),
        timestamp: new Date(),
      },
    ]);

    // Clear input field
    setInput("");

    // Focus on the text box
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }, 100);

    // Show confirmation message
    setUploadResult({
      success: true,
      message: "Started a new conversation",
    });
  };

  // Track document processing progress
  const { run: processingRun } = useRealtimeRun(
    uploadResult?.runId,
    {
      accessToken: uploadResult?.publicAccessToken,
      enabled: !!uploadResult?.runId && !!uploadResult?.publicAccessToken,
    }
  );

  // Update processing state when run data changes
  useEffect(() => {
    if (!processingRun) return;

    const statusMessage = getStatusMessage(
      processingRun.status,
      processingRun.error?.message
    );
    const progress = processingRun.metadata?.progress
      ? Number(processingRun.metadata.progress)
      : undefined;

    // Update or add processing message
    setMessages((prev) => {
      const existingIndex = prev.findIndex((m) => m.type === "processing");
      const newMessage: Message = {
        id: `processing-${processingRun.id}`,
        role: "system",
        content: ``,
        timestamp: new Date(),
        type:
          processingRun.status === "COMPLETED"
            ? "success"
            : processingRun.status === "FAILED" ||
              processingRun.status === "CANCELED" ||
              processingRun.status === "CRASHED" ||
              processingRun.status === "TIMED_OUT"
            ? "error"
            : "processing",
        progress: progress,
      };

      if (existingIndex >= 0) {
        // Update existing processing message
        const updated = [...prev];
        updated[existingIndex] = newMessage;
        return updated;
      } else {
        // Add new processing message
        return [...prev, newMessage];
      }
    });

    // Clean up processing message if completed or failed
    if (
      ["COMPLETED", "FAILED", "CANCELED", "CRASHED", "TIMED_OUT"].includes(
        processingRun.status
      )
    ) {
      setUploadResult({
        success: processingRun.status === "COMPLETED",
        message: statusMessage,
      });
    }
  }, [processingRun]);

  // Helper function to get user-friendly status message
  const getStatusMessage = (status: string, errorMessage?: string): string => {
    switch (status) {
      case "QUEUED":
        return "Document is queued for processing...";
      case "EXECUTING":
      case "REATTEMPTING":
        return "Processing document...";
      case "COMPLETED":
        return "Document processed successfully! You can now ask questions about it.";
      case "FAILED":
      case "CANCELED":
      case "CRASHED":
      case "INTERRUPTED":
      case "SYSTEM_FAILURE":
      case "TIMED_OUT":
        return `Processing failed: ${
          errorMessage || status.toLowerCase().replace("_", " ")
        }`;
      case "WAITING_FOR_DEPLOY":
        return "Waiting for deployment...";
      case "FROZEN":
      case "DELAYED":
      case "EXPIRED":
        return "Processing delayed. Please try again later.";
      default:
        return "Processing your document...";
    }
  };

  return (
    <>
      {/* Hero Section */}
      <CoraaHeroSection />
      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 min-w-0">
          <div className="space-y-6  relative">
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-4 backdrop-blur-2xl bg-opacity-0 bg-white absolute top-0 left-0 right-0 z-10">
                <div className="flex items-center justify-between">
                  <div className="flex justify-center mt-2 mb-3">
                    <div className="w-full max-w-3xl">
                      <SuggestedPromptsBar
                        onPromptClick={(text) => setInput(text)}
                        autoSubmit={true}
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3"></div>
                  <div className="flex items-center gap-2">
                    {isUploading && (
                      <div className="flex items-center text-sm text-blue-600 animate-pulse">
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        <span>Uploading document...</span>
                      </div>
                    )}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <Settings className="h-5 w-5" />
                          <span className="sr-only">Settings</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => {
                            if (fileInputRef.current) {
                              fileInputRef.current.value = "";
                              fileInputRef.current.click();
                            }
                          }}
                        >
                          <Upload className="mr-2 h-4 w-4" />
                          <span>Upload Documents</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => fetchCheckpoints(1)}>
                          <Database className="mr-2 h-4 w-4" />
                          <span>View Training Data</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handleClearConversation}>
                          <RefreshCw className="mr-2 h-4 w-4" />
                          <span>New Conversation</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setStreamingEnabled(!streamingEnabled)}
                        >
                          <div className="mr-2 h-4 w-4 flex items-center justify-center">
                            {streamingEnabled ? "⚡" : "⏸️"}
                          </div>
                          <span>
                            {streamingEnabled ? "Disable" : "Enable"} Streaming
                          </span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setShowClearConfirmation(true)}
                          className="text-red-600"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          <span>Clear Training Data</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <input
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    ref={fileInputRef}
                    accept=".pdf,.doc,.docx,.txt"
                    onClick={(e) => {
                      // Reset the value to ensure onChange fires even if the same file is selected again
                      const target = e.target as HTMLInputElement;
                      target.value = "";
                    }}
                  />
                </div>
              </CardHeader>
              <CardContent className="p-0 relative z-9">
                <div className="flex flex-col h-[calc(100vh-16rem)]">
                  <div
                    className="flex-1 overflow-y-auto p-4 space-y-4  pt-[100px]"
                    style={{ scrollBehavior: "smooth" }}
                  >
                    {processedMessages.map((message) => (
                      <MessageComponent
                        key={message.id}
                        message={message}
                        setSelectedImage={setSelectedImage}
                        markdownComponents={markdownComponents}
                      />
                    ))}
                    {isLoading && (
                      <div className="flex justify-start">
                        <div className="max-w-[80%] rounded-lg p-4 bg-muted">
                          <div className="flex items-center gap-2 mb-1">
                            <Avatar className="h-6 w-6">
                              <AvatarImage src="/images/coraa-mascot.png" />
                              <AvatarFallback className="bg-primary/10 text-primary">
                                <BotIcon className="h-4 w-4" />
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-xs opacity-70">
                              Coraa • {format(new Date(), "h:mm a")}
                            </span>
                          </div>
                          
                          {streamingStatus ? (
                            <div className="flex items-center gap-2">
                              {streamingStatus.phase === "connecting" && (
                                <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                              )}
                              {streamingStatus.phase === "thinking" && (
                                <div className="flex space-x-1">
                                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce"></div>
                                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                                  <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                                </div>
                              )}
                              {streamingStatus.phase === "tool_calling" && (
                                <div className="w-3 h-3 border-2 border-orange-500 border-r-transparent rounded-full animate-spin" />
                              )}
                              {streamingStatus.phase === "tool_completed" && (
                                <div className="w-3 h-3 bg-green-500 rounded-full flex items-center justify-center">
                                  <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                                </div>
                              )}
                              {streamingStatus.phase === "generating" && (
                                <Loader2 className="h-3 w-3 animate-spin text-green-500" />
                              )}
                              <span className="text-sm">
                                {streamingStatus.message}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center">
                              <div className="flex items-center space-x-1">
                                <div
                                  className="w-2 h-2 rounded-full bg-[#2E7D32] animate-bounce"
                                  style={{ animationDelay: "0ms" }}
                                ></div>
                                <div
                                  className="w-2 h-2 rounded-full bg-[#2E7D32] animate-bounce"
                                  style={{ animationDelay: "150ms" }}
                                ></div>
                                <div
                                  className="w-2 h-2 rounded-full bg-[#2E7D32] animate-bounce"
                                  style={{ animationDelay: "300ms" }}
                                ></div>
                              </div>
                              <span className="ml-2">typing...</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                  <div className="border-t p-4">
                    <form onSubmit={handleSubmit} className="flex gap-2">
                      <div className="flex items-center gap-2 w-full">
                        <Textarea
                          ref={textareaRef}
                          className="flex-1 resize-none min-h-[44px] max-h-[200px] rounded-xl border border-gray-200 text-base px-4 py-2 shadow-sm focus:ring-2 focus:ring-green-200 focus:border-green-400 transition placeholder:text-gray-400 bg-white"
                          placeholder="Type your message..."
                          value={input}
                          onChange={(e) => setInput(e.target.value)}
                          onKeyDown={handleKeyDown}
                          rows={1}
                          disabled={isLoading}
                        />
                        <Button
                          type="button"
                          className="h-11 w-11 rounded-full flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-500"
                          aria-label="Attach Document"
                          onClick={() => {
                            if (fileInputRef.current) {
                              fileInputRef.current.value = "";
                              fileInputRef.current.click();
                            }
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M21 12.79V17a5 5 0 01-5 5H7a5 5 0 01-5-5V7a5 5 0 015-5h4.21a2 2 0 011.42.59l7.78 7.78a2 2 0 01.59 1.42z"
                            />
                          </svg>
                        </Button>
                        <Button
                          type="submit"
                          className="ml-2 h-11 w-11 rounded-full flex items-center justify-center bg-green-600 hover:bg-green-700 text-white shadow-lg disabled:bg-gray-200 disabled:text-gray-400"
                          disabled={!input.trim() || isLoading}
                        >
                          {isLoading ? (
                            <Loader2 className="animate-spin h-6 w-6" />
                          ) : (
                            <SendIcon className="h-5 w-5" />
                          )}
                        </Button>
                      </div>
                    </form>
                    <p className="text-xs text-muted-foreground mt-2">
                      Coraa can make mistakes. Consider checking important
                      information.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Clear Training Data Confirmation Dialog */}
            {showClearConfirmation && (
              <Dialog
                open={showClearConfirmation}
                onOpenChange={setShowClearConfirmation}
              >
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Clear Training Data</DialogTitle>
                    <DialogDescription>
                      Are you sure you want to clear all training data? This
                      action cannot be undone and will reset Coraa's knowledge
                      to default settings.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setShowClearConfirmation(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={handleClearTrainingData}
                    >
                      Clear Data
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}

            {/* Training Data Modal - Matching GraphPage.tsx implementation */}
            {showTrainingDataModal && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-lg max-w-3xl w-full max-h-[80vh] flex flex-col">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold">Training Data</h3>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setShowTrainingDataModal(false)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  {isLoadingTrainingData && trainingData.length === 0 ? (
                    <div className="flex justify-center items-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                    </div>
                  ) : trainingData.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>No training data available</p>
                    </div>
                  ) : (
                    <div className="flex-1 overflow-hidden flex flex-col">
                      <div
                        className="overflow-y-auto overflow-x-auto flex-1 pr-1"
                        style={{ scrollbarWidth: "thin" }}
                      >
                        <table className="w-full border-collapse">
                          <thead className="sticky top-0 bg-white z-10">
                            <tr className="border-b">
                              <th className="text-left py-2 px-4">
                                Training Data (URL)
                              </th>
                              <th className="text-left py-2 px-4">
                                Training Date
                              </th>
                              <th className="text-center py-2 px-4">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Array.isArray(trainingData) &&
                              trainingData.map((item) => (
                                <tr
                                  key={item.document_id}
                                  className="border-b hover:bg-gray-50"
                                >
                                  <td className="py-3 px-4">
                                    <div className="flex items-center gap-2">
                                      <ExternalLink className="h-4 w-4 text-gray-400" />
                                      <a
                                        href={item.source_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="truncate max-w-md text-blue-600 hover:text-blue-800 hover:underline"
                                        title={item.source_url}
                                      >
                                        {(() => {
                                          const filename =
                                            item.source_url.split("/").pop() ||
                                            item.source_url;
                                          return filename.length > 20
                                            ? `${filename.substring(0, 17)}...`
                                            : filename;
                                        })()}
                                      </a>
                                    </div>
                                  </td>
                                  <td className="py-3 px-4">
                                    {format(
                                      new Date(item.created_at),
                                      "MMM d, yyyy h:mm a"
                                    )}
                                  </td>
                                  <td className="py-3 px-4 text-center">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                      onClick={() =>
                                        handleDeleteTrainingData(
                                          item.document_id
                                        )
                                      }
                                      disabled={
                                        isDeletingTrainingData ===
                                        item.document_id
                                      }
                                    >
                                      {isDeletingTrainingData ===
                                      item.document_id ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <>
                                          <Trash2 className="h-4 w-4 mr-1" />
                                          Remove
                                        </>
                                      )}
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>

                        {/* Infinite scroll trigger */}
                        {pagination.hasMore && !isLoadingTrainingData && (
                          <div className="flex justify-center py-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={loadMoreTrainingData}
                              disabled={isLoadingTrainingData}
                            >
                              Load More
                            </Button>
                          </div>
                        )}

                        {isLoadingTrainingData && trainingData.length > 0 && (
                          <div className="flex justify-center py-4">
                            <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                          </div>
                        )}
                      </div>

                      <div className="text-center text-sm text-gray-500 mt-2 py-2 border-t">
                        Showing {trainingData.length} of {pagination.total}{" "}
                        items
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end mt-4 pt-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowTrainingDataModal(false)}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Upload Result Toast */}
            {uploadResult && (
              <div
                className={`fixed bottom-4 right-4 p-4 rounded-md shadow-lg ${
                  uploadResult.success
                    ? "bg-green-50 text-green-700 border border-green-200"
                    : "bg-red-50 text-red-700 border border-red-200"
                }`}
              >
                <div className="flex items-center gap-2">
                  {uploadResult.success ? (
                    <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3 text-green-700"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  ) : (
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3 text-red-700"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  )}
                  <span>{uploadResult.message}</span>
                  <button
                    className="ml-auto text-gray-500 hover:text-gray-700"
                    onClick={() => setUploadResult(null)}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Smart Sidebar - only on large screens */}
        <SmartSidebar session={session} profile={profile} />
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <Dialog
          open={!!selectedImage}
          onOpenChange={() => setSelectedImage(null)}
        >
          <DialogContent className="max-w-4xl w-full p-0 overflow-hidden bg-transparent border-0">
            <div className="relative bg-white rounded-lg shadow-xl overflow-hidden">
              <div className="absolute top-2 right-2 z-10">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-full bg-black/50 text-white hover:bg-black/70"
                  onClick={() => setSelectedImage(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center justify-center p-2 bg-black/10 backdrop-blur-sm">
                <img
                  src={selectedImage}
                  alt="Full size image"
                  className="max-h-[80vh] max-w-full object-contain"
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default AskCora;
